import { cn } from "@lab/src/lib/util";
import { Checkbox } from "../../../components/ui/checkbox";
import { ArchiveOverview } from "../../../store/Archive/archive.store";
import { ARCHIVE_KIND_LABELS, getArchiveKindLabel } from "../archive.utils";

/** Organisation-wide scopes have no counts; every kind is offered as a filter. */
const ALL_KINDS = Object.keys(ARCHIVE_KIND_LABELS).map((kind) => ({
	kind,
	count: null,
}));

interface ArchiveFacetSidebarProps {
	overview: ArchiveOverview | null;
	failed: boolean;
	filterYear: number | null;
	filterKinds: string[];
	onSelectYear: (year: number) => void;
	onToggleKind: (kind: string) => void;
}

export const ArchiveFacetSidebar: React.FC<ArchiveFacetSidebarProps> = ({
	overview,
	failed,
	filterYear,
	filterKinds,
	onSelectYear,
	onToggleKind,
}) => {
	if (!overview) {
		if (failed) {
			return (
				<aside className="w-52 shrink-0 pr-2 text-sm text-muted-foreground">
					Filter konnten nicht geladen werden.
				</aside>
			);
		}
		return null;
	}

	const kinds = overview.kinds ?? ALL_KINDS;

	return (
		<aside
			className="flex w-52 shrink-0 flex-col gap-6 overflow-y-auto pr-2"
			aria-label="Filter"
		>
			<section>
				<h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
					Jahr
				</h3>
				<ul className="flex flex-col">
					{overview.years.map(({ year, count }) => {
						const active = filterYear === year;
						return (
							<li key={year}>
								<button
									type="button"
									className={cn(
										"flex w-full items-center justify-between rounded px-2 py-1 text-sm hover:bg-accent",
										active &&
											"bg-accent font-medium text-accent-foreground"
									)}
									onClick={() => onSelectYear(year)}
									aria-pressed={active}
									aria-label={`Jahr ${year} filtern`}
									data-testid={`archive-facet-year-${year}`}
								>
									<span>{year}</span>
									{count !== null && (
										<span className="text-xs text-gray-400">
											{count.toLocaleString("de-DE")}
										</span>
									)}
								</button>
							</li>
						);
					})}
				</ul>
			</section>

			<section>
				<h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
					Art
				</h3>
				<ul className="flex flex-col gap-0.5">
					{kinds.map(({ kind, count }) => {
						const checked = filterKinds.includes(kind);
						return (
							<li key={kind}>
								<label
									className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 text-sm hover:bg-accent"
									data-testid={`archive-facet-kind-${kind}`}
								>
									<span className="flex items-center gap-2">
										<Checkbox
											checked={checked}
											onCheckedChange={() =>
												onToggleKind(kind)
											}
											aria-label={`${getArchiveKindLabel(
												kind
											)} filtern`}
										/>
										{getArchiveKindLabel(kind)}
									</span>
									{count !== null && (
										<span className="text-xs text-gray-400">
											{count.toLocaleString("de-DE")}
										</span>
									)}
								</label>
							</li>
						);
					})}
				</ul>
			</section>
		</aside>
	);
};
